import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { NavigationService } from 'src/app/navigation/navigation.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { ModuleManager } from 'src/shared/models/module-manager';
import { ModuleManagerRefreshType } from 'src/shared/models/module-manager/module-manager-options.model';
import { CxonePopoutComponent, PopoutContext } from '../../toolbar-items/cxone-popout/cxone-popout.component';
import { FormsCalibrationData, FormsCalibrationRow } from './forms-calibration.model';
import { FormsCalibrationService } from './forms-calibration.service';
import { Chart, ChartConfiguration, Tooltip } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';

@Component({
    selector: 'cv-forms-calibration',
    templateUrl: './forms-calibration.component.html',
    styleUrls: ['./forms-calibration.component.scss']
})
export class FormsCalibrationComponent implements OnInit {
    @Input() moduleManager: ModuleManager;
    @Input() moduleKey: string;
    @ViewChild(BaseChartDirective) chartObject: BaseChartDirective;

    data: FormsCalibrationData = null;
    isDataLoading = false;
    isFirstLoad = true;
    noData = false;
    width = null; //chart width?
    popoutContextUrl = "";
    popoutContext: PopoutContext = {
        url: () => this.popoutContextUrl,
        isVisible: () => true // currently always visible
    };
    chartConfig = null;
    variance = '';

    constructor(
        private cvLocalizationService: CVLocalizationService,
        private navigationService: NavigationService,
        private service: FormsCalibrationService,
        private config: CvConfigurationService
    ) { }

    async ngOnInit(): Promise<void> {
        await this.cvLocalizationService.GetSection('Calibration_Widgets');

        this.moduleManager.setOptions({
            onSettingsApplied: () => {
                this.loadData(false);
            },
            hasRefresh: true,
            refreshType: ModuleManagerRefreshType.Auto,
            onRefresh: isAutoRefresh => {
                this.loadData(isAutoRefresh);
            },
            canRefresh: () => !this.isDataLoading,
            onExport: (format, isCXOne) => this.service.getExport(this.moduleKey, format, isCXOne)
        });
        this.popoutContextUrl =
            `${this.navigationService.cxoneTokenUrls?.niceBaseUrl ? this.navigationService.cxoneTokenUrls.niceBaseUrl : ''}` +
            `/qm/monitoring/#/calibrations`;
        this.moduleManager.addToolbarItem(CxonePopoutComponent, this.popoutContext);
        this.variance = await this.cvLocalizationService.GetLocalText('Calibration_Widgets', 'Variance', 'Variance');
    }

    private async loadData(isAutoRefresh) {
        if (this.isDataLoading) return;
        this.isDataLoading = true;

        if (!isAutoRefresh) this.moduleManager.showLoader = true;

        try {
            this.data = await this.service.loadData(this.moduleKey, isAutoRefresh, this.isFirstLoad);
            this.moduleManager.setAutoRefreshReady();
        } catch (error) {
            this.moduleManager.handleLoadError(error, isAutoRefresh);
        } finally {
            this.isFirstLoad = false;
            this.moduleManager.showLoader = false;
            this.isDataLoading = false;
        }

        this.createChart();
    }

    private async createChart() {

        const formNames: string[] = [];
        const varianceData: number[] = [];
        const backgroundColors = [];

        const formsData = new Map<string, FormsCalibrationRow>(Object.entries(this.data.Forms));
        this.noData = formsData.size === 0;
        if (this.noData) {
            return;
        }
        const sortedFormsData = Array.from(formsData);
        sortedFormsData.sort((a, b) => {
            if (Math.abs(a[1].AverageVariance) > Math.abs(b[1].AverageVariance))
                return -1;

            if (Math.abs(a[1].AverageVariance) < Math.abs(b[1].AverageVariance))
                return 1;

            return 0;
        });

        for (let i = 0; i < 6; i++) {
            if (!sortedFormsData[i]) break;
            formNames.push(sortedFormsData[i][1].FormName);
            varianceData.push(sortedFormsData[i][1].AverageVariance / 100);
            if (Math.abs(sortedFormsData[i][1].AverageVariance) > this.moduleManager.customSettings.varianceScaleValue.getValue())
                backgroundColors.push('rgb(232,116,92)');
            else
                backgroundColors.push('rgb(240,236,236)');
        }

        const chartData = {
            labels: formNames,
            datasets: [{
                label: await this.cvLocalizationService.GetLocalText('Calibration_Widgets', 'Variance'),
                backgroundColor: backgroundColors,
                borderColor: backgroundColors,
                data: varianceData,
            }],
        };

        Tooltip.positioners.customPositionerCalibration = (elements) => {
            if (!elements.length) {
                return false;
            }
            return {
                x: elements[0].element.x,
                y: (elements[0].element as any).$datalabels[0].$context.chart.height - 28,
            };
        };

        this.chartConfig = {
            type: 'bar',
            data: chartData,
            options: {
                responsive: true,
                animation: {
                    onComplete: (chart) => {
                        if (chart.initial && chart.chart) {
                            const chartWidth = chart.chart.width;
                            const numBars = chart.chart.data.labels.length;
                            const barWidth = chartWidth / numBars;
                            const fontSize = 14;
                            const charWidth = fontSize * 0.6;
                            let maxLength = Math.floor(barWidth / charWidth) - 3;
                            maxLength = maxLength > 3 ? maxLength - 3 : maxLength;
                            chart.chart.data.labels.forEach((label: string, ind) => {
                                let retValue = label;
                                if (retValue.length > maxLength) {
                                    retValue = `${retValue.substring(0, maxLength).trim()}...`;
                                }
                                chart.chart.scales.x.ticks[ind].label = retValue;
                            })
                            chart.chart.update(); 
                        }
                    }
                },
                onResize: (chart: Chart, newSize: { width: number; height: number }) => { // may need to fix
                    this.width = newSize.width;
                },
                maintainAspectRatio: false,
                plugins: {
                    emptyChart: {
                        disableCxone: this.config.isCxOne,
                    },
                    legend: {
                        display: false,
                    },
                    tooltip: {
                        position: 'customPositionerCalibration',
                        intersect: false,
                        mode: 'index',
                        titleMarginBottom: 0,
                        backgroundColor: '#FFFFFF',
                        titleColor: '#2E2E2E',
                        titleFont: {
                            size: 13,
                            weight: 'normal',
                            family: 'Open Sans, Regular',
                        },
                        yAlign: 'bottom',
                        bodySpacing: 0,
                        borderColor: '#D0D2D3',
                        borderWidth: 1,
                        cornerRadius: 0,
                        callbacks: {
                            title: (tooltipItem) => tooltipItem[0].label,
                            label: () => '',
                        },
                    },
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        ticks: {
                            callback: value => value.toLocaleString('en-US', { style: 'percent' }),
                        }
                    },
                    x: {
                        ticks: {
                            display: true,
                            maxRotation: 0,
                            minRotation: 0,
                            callback: value => {
                                const chartWidth = this.chartObject?.chart.width;
                                const numBars = this.chartObject?.chart.data.labels.length;
                                const barWidth = chartWidth / numBars;
                                const fontSize = 14;
                                const charWidth = fontSize * 0.6;
                                let maxLength = Math.floor(barWidth / charWidth) - 3;
                                maxLength = maxLength > 3 ? maxLength - 3 : maxLength;
                                const retValue = chartData.labels[value];
                                if (retValue === "") {
                                    return retValue;
                                } else if (retValue.length < maxLength) {
                                    return retValue;
                                } else {
                                    return `${retValue.substring(0, maxLength).trim()}...`;
                                }
                            }
                        }
                    },
                },
            },
            plugins: [{
                id: "draw-bar-labels",
                afterDatasetsDraw: (chartInstance: any) => {
                    const map = new Map<string, FormsCalibrationRow>();
                    Object.entries(this.data.Forms).forEach(value => {
                        map.set(value[0], value[1]);
                    });
                    if (map.size === 0) return;
                    const ctx = chartInstance.ctx;
                    const width = chartInstance.width;
                    const size = Math.round(width / 20);

                    const meta = chartInstance.getDatasetMeta(0);
                    if (!meta.hidden) {
                        meta.data.forEach((element) => {
                            if (element.hidden) return;
                            ctx.fillStyle = '#000';
                            ctx.font = 'bold ' + size + 'px';
                            ctx.textAlign = 'center';
                            ctx.textBaseline = 'middle';
                            const position = element.tooltipPosition();
                            const index = element.$datalabels[0].$context.dataIndex;
                            const value = element.$datalabels[0].$context.dataset.data[index];
                            const displayValue = Math.round(value * 100).toString() + '%';
                            if (value > 0) ctx.fillText(displayValue, position.x, position.y + 16);
                            else ctx.fillText(displayValue, position.x, position.y - 16);
                        });
                    }
                }
            }],
        } as ChartConfiguration;

    }
}
